import { useCallback, useTransition } from 'react'
import { usePostHogEvent } from '../../../../../PostHog/index'
import { formatErrorForLogging, getLogger } from '../../../../../../log/index'
import type { CollectionsTab } from './PlaylistTabContainer'
import { TABS } from './PlaylistTabContainer'

/**
 * Consolidates six of the seven inline useCallbacks
 * (handleTabClick, handleSearchChange, handleSearchSubmit,
 * handlePlaylistCreated, handleFollowingToastComplete,
 * handleTabRenderError) plus `useTransition` ownership.
 *
 * The seventh inline callback (`handleMutationError`) stays in the
 * container because `usePlaylistFollowMutations` consumes it as a
 * constructor arg BEFORE this hook runs — splitting it out here
 * would force the container to either re-thread it or duplicate the
 * showSaveErrorToast call. Three lines in the container is cheaper.
 *
 * The hook owns BOTH PostHog event hooks (`community_library_tab_changed`,
 * `community_playlist_created`) per the project's track* naming
 * convention.
 */

type CommunityLibraryTab = 'my_playlists' | 'following' | 'discover'

export interface UsePlaylistTabHandlersOptions {
  collectionsTab: CollectionsTab
  onCollectionsTabChange: (tab: CollectionsTab) => void
  onCollectionsSearch: (value: string) => void
  setSearchTrigger: (updater: (prev: number) => number) => void
  /** From usePlaylistFollowMutations — drops any pending unfollow toast on tab change. */
  flushPendingUnfollow: (tab: CollectionsTab) => void
  /** From usePlaylistFollowMutations — clears the toast on completion. */
  setUnfollowToast: (
    updater: (prev: { playlistId: string; playlistName: string } | null) => (
      { playlistId: string; playlistName: string } | null
    ),
  ) => void
  refetchPlaylists?: () => void
}

export interface UsePlaylistTabHandlersResult {
  handleTabClick: (tab: CollectionsTab) => void
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleSearchSubmit: () => void
  handlePlaylistCreated: (method: string, details?: unknown) => void
  handleFollowingToastComplete: (playlistId: string) => void
  handleTabRenderError: (error: Error, info: { componentStack?: string | null }) => void
}

export default function usePlaylistTabHandlers (
  options: UsePlaylistTabHandlersOptions,
): UsePlaylistTabHandlersResult {
  const {
    collectionsTab,
    onCollectionsTabChange,
    onCollectionsSearch,
    setSearchTrigger,
    flushPendingUnfollow,
    setUnfollowToast,
    refetchPlaylists,
  } = options

  // Wrap the tab-state transition so the new tab's data hook can suspend /
  // refetch without blocking input.
  const [, startTabTransition] = useTransition()

  // PostHog wiring — track* naming convention per the project memory.
  const trackLibraryTabChanged = usePostHogEvent('community_library_tab_changed')
  const trackPlaylistCreated = usePostHogEvent('community_playlist_created')

  const handleTabClick = useCallback((tab: CollectionsTab) => {
    flushPendingUnfollow(collectionsTab)
    let posthogTab: CommunityLibraryTab
    if (tab === TABS.MY_PLAYLISTS) posthogTab = 'my_playlists'
    else if (tab === TABS.FOLLOWING) posthogTab = 'following'
    else posthogTab = 'discover'
    trackLibraryTabChanged(posthogTab)
    startTabTransition(() => {
      onCollectionsTabChange(tab)
    })
    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    }
  }, [onCollectionsTabChange, collectionsTab, flushPendingUnfollow, trackLibraryTabChanged])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    onCollectionsSearch(value)
    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    }
  }, [onCollectionsSearch])

  const handleSearchSubmit = useCallback(() => {
    if (collectionsTab === TABS.DISCOVER) {
      setSearchTrigger((prev) => prev + 1)
    }
  }, [collectionsTab, setSearchTrigger])

  const handlePlaylistCreated = useCallback((
    method: string,
    details?: unknown,
  ) => {
    // PostHog event signature is broad here — the modal's `onCreated`
    // contract carries its own typed args, which TS already enforces at
    // the call site upstream.
    (trackPlaylistCreated as unknown as (m: string, d?: unknown) => void)(method, details)
    if (refetchPlaylists) refetchPlaylists()
  }, [trackPlaylistCreated, refetchPlaylists])

  const handleFollowingToastComplete = useCallback((playlistId: string) => {
    setUnfollowToast((prev) => (
      prev && prev.playlistId === playlistId ? null : prev
    ))
  }, [setUnfollowToast])

  const handleTabRenderError = useCallback((
    error: Error,
    info: { componentStack?: string | null },
  ) => {
    const logger = getLogger()
    const payload = {
      ...formatErrorForLogging(error, 'Community library tab render error'),
      componentStack: info.componentStack ?? undefined,
      tab: collectionsTab,
    }
    if (logger?.error) {
      logger.error(payload)
    } else {
      // eslint-disable-next-line no-console
      console.error(payload)
    }
  }, [collectionsTab])

  return {
    handleTabClick,
    handleSearchChange,
    handleSearchSubmit,
    handlePlaylistCreated,
    handleFollowingToastComplete,
    handleTabRenderError,
  }
}
